/**
 * API модуля chat: чат, сообщения, промпты, расчёт контекста
 */
import apiReq from 'core/api';
import { normalizeBackendUtcIsoString } from 'utils/dateUtils';
import type { DocumentPreview } from 'types/contextDocuments';
import type {
  Message,
  Prompt,
  SendMessageResponse,
  ClearChatResponse,
  UsageResponse,
  ChatListing,
  BackendMessage,
} from './model';

interface ChatsListResponse {
  count: number;
  items: ChatListing[];
}

const BASE = 'projects/chat';

/** Получить историю чата по проекту */
export const getChat = async (projectId: string): Promise<Message[] | undefined> => {
  const data = await apiReq<Message[]>(`${BASE}?project_id=${projectId}`);
  if (!Array.isArray(data)) return [];
  return data.map((m) =>
    m.created_at ? { ...m, created_at: normalizeBackendUtcIsoString(m.created_at) } : m
  );
};

/** Отправить сообщение (запуск стриминга) */
export const sendMessage = async (
  projectId: string,
  roleId: number | null,
  body: Record<string, unknown>
): Promise<SendMessageResponse | undefined> => {
  return apiReq<SendMessageResponse>(
    `${BASE}?project_id=${projectId}&role_id=${roleId}`,
    'POST',
    body
  );
};

/** Остановить текущий запрос */
export const stopChat = async (taskId: string): Promise<unknown> => {
  return apiReq('projects/chat/stop', 'POST', { task_id: taskId });
};

/** Очистить чат по проекту */
export const clearChat = async (projectId: string): Promise<ClearChatResponse | undefined> => {
  return apiReq<ClearChatResponse>('projects/chat/clear', 'POST', { project_id: projectId });
};

/** Расчёт использования контекста */
export const calcUsage = async (
  chatId: string,
  roleId: number | null,
  body: Record<string, unknown>
): Promise<UsageResponse | undefined> => {
  const params = new URLSearchParams();
  if (roleId != null) params.set('role_id', String(roleId));
  return apiReq<UsageResponse>(
    `chat/${chatId}/messages/calc-usage?${params.toString()}`,
    'POST',
    body
  );
};

// ——— Multi-chat API ———

/** Получить список всех чатов пользователя */
export const listChats = async (projectId: string): Promise<ChatListing[]> => {
  const data = await apiReq<ChatsListResponse>(`chats?project_id=${projectId}`);
  return data?.items ?? [];
};

/** Создать новый чат */
export const createChatSession = async (
  projectId: string,
  title?: string
): Promise<ChatListing | undefined> => {
  return apiReq<ChatListing>('chats/create', 'POST', {
    project_id: projectId,
    ...(title !== undefined ? { title } : {}),
  });
};

/** Получить историю сообщений по ID чата */
export const getChatMessagesById = async (chatId: string): Promise<Message[]> => {
  const data = await apiReq<BackendMessage[]>(`chat/${chatId}/messages`);
  if (!Array.isArray(data)) return [];
  return data.map((m) => ({
    role: (m.role?.toLowerCase() === 'user' ? 'User' : 'AI') as Message['role'],
    content: m.content,
    created_at: m.created_at
      ? normalizeBackendUtcIsoString(m.created_at)
      : new Date().toISOString(),
    attached_document_ids: Array.isArray(m.attached_document_ids) ? m.attached_document_ids : [],
  }));
};

/** Отправить сообщение в конкретный чат */
export const sendMessageToChat = async (
  chatId: string,
  body: Record<string, unknown>,
  roleId: number
): Promise<SendMessageResponse | undefined> => {
  return apiReq<SendMessageResponse>(
    `chat/${chatId}/messages/create?role_id=${roleId}`,
    'POST',
    body
  );
};

/** Очистить историю конкретного чата */
export const clearChatById = async (chatId: string): Promise<ClearChatResponse | undefined> => {
  return apiReq<ClearChatResponse>(`chat/${chatId}/clear`, 'POST');
};

/** Остановить стриминг в конкретном чате */
export const stopChatById = async (chatId: string, taskId: string): Promise<unknown> => {
  return apiReq(`chat/${chatId}/stop`, 'POST', { task_id: taskId });
};

/** Удалить чат */
export const deleteChatById = async (chatId: string): Promise<void> => {
  await apiReq<void>(`chat/${chatId}/delete`, 'DELETE');
};

/** Переименовать чат */
export const renameChatById = async (
  chatId: string,
  title: string
): Promise<ChatListing | undefined> => {
  return apiReq<ChatListing>(`chat/${chatId}/rename`, 'POST', { title });
};

/** Превью документа docling: плоский список заголовков для выбора фрагментов */
export const fetchDocumentPreview = async (documentId: string): Promise<DocumentPreview> => {
  const data = await apiReq<DocumentPreview>(`projects/context-documents/${documentId}/preview`);
  if (!data) throw new Error('Empty response');
  return data;
};

// ——— Промпты (сохранённые шаблоны сообщений) ———

export const getPrompts = async (projectId: string, roleId: number): Promise<Prompt[]> => {
  const response = await apiReq<Prompt[]>(`prompts?project_id=${projectId}&role_id=${roleId}`);
  return response ?? [];
};

export const savePrompt = async (
  projectId: string,
  roleId: number,
  content: string
): Promise<Prompt> => {
  const response = await apiReq<Prompt>('prompts/create', 'POST', {
    project_id: projectId,
    role_id: roleId,
    content,
  });
  if (!response) throw new Error('Empty response');
  return response;
};

export const deletePrompt = async (promptId: number): Promise<void> => {
  await apiReq(`prompts/delete?id=${promptId}`, 'DELETE');
};
